import React, { useState } from 'react';
import { FaUserCog } from "react-icons/fa";
import { IconBaseProps } from 'react-icons';
import { Globe } from "react-bootstrap-icons";

import { logout } from '../../services/auth.api';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { getRedirecturl } from "../../utils/oidcConfig";
import { getUserRole, isTokenValid } from "../../utils/authUtils";
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { DEFAULT_LANGUAGE } from '../../utils/constants';
import User from '../../types/user';


import logo from '../../assets/images/SCSLogo.svg'
import dashIcon from '../../assets/images/ic_dashboard_24px.svg'
import logoutIcon from '../../assets/images/ic_power_settings_new_24px.svg'
import ChecklistInActiveIcon from "../../assets/images/checklist-inactive-icon.svg";
import ChecklistActiveIcon from "../../assets/images/checklist-active-icon.svg";
import DashInactiveIcon from "../../assets/images/dashboard-inactive-icon.svg";
import settingIcon from '../../assets/images/ic_settings_24px.svg'
import HomeIcon from "../../assets/images/home-icon.svg";
import feedbackIcon from "../../assets/images/feedback-icon.svg";
import './sidenav.components.css';

const Sidebar = () => {
  const user = useSelector((state: RootState) => state.users.loginUser);
  const [footerMenuVisible, setFooterMenuVisible] = useState(false);
  const [activePortal, setActivePortal] = useState<string | null>(null);
  const navigate = useNavigate();
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const userRoles = getUserRole(user as User);
  const FaUserCogIcon = FaUserCog as React.ComponentType<IconBaseProps>;

  const matchCertificates = useMatch("/certificates");
  const matchDashboard = useMatch("/dashboard/*");
  const matchLogoRequest = useMatch("/logoRequest/*");
  const matchHomePage = useMatch("/home");
  const matchManageUserPage = useMatch("/manageUser");
  const matchBannerManagementUserPage = useMatch("/manageBanners");

  const isLogoPortalActive =
    !!matchCertificates || !!matchDashboard || !!matchLogoRequest;

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
    document.body.classList.toggle("sidebar-hide", !sidebarVisible);
  };

  /**
   * On Manage user button click
   */
  const onManageUserClick = () => {
    navigate('/manageUser');
  }

  /**
   * On home button click
   */
  const onHomeClick = () => {
    navigate('/home');
  }

  /**
   * On Manage banners button click
   */
  const onBannerManagementClick = () => {
    navigate('/manageBanners');
  }

  const handleSelectionPortal = (portalName: string) => {
    if (portalName == "Logo Portal") {
      navigate('/certificates');
    } else {
      const redirectUrl = getRedirecturl() as string;
      let portalUrl = "/auth-callback";

      const token = localStorage.getItem("access_token");
      const access_token_expiry = localStorage.getItem("access_token_expiry");
      if (!isTokenValid(token)) {
        logout();
        return;
      }
      if (redirectUrl === "http://localhost:3000") {
        switch (portalName) {
          case "Auditor Portal":
            portalUrl = `${redirectUrl.split(":")[1]}:3001${portalUrl}`;
            break;
          case "Logo Portal":
            portalUrl = `${redirectUrl.split(":")[1]}:3003${portalUrl}`;
            break;
          case "Customer Portal":
            portalUrl = `${redirectUrl.split(":")[1]}:3002${portalUrl}`;
            break;
          default:
            portalUrl = redirectUrl;
            break;
        }
      } else {
        switch (portalName) {
          case "Auditor Portal":
            portalUrl =
              process.env.REACT_APP_AUDITOR_PORTAL_URL + portalUrl || "";
            break;
          case "Logo Portal":
            portalUrl = process.env.REACT_APP_LOGO_PORTAL_URL + portalUrl || "";
            break;
          case "Customer Portal":
            portalUrl =
              process.env.REACT_APP_CUSTOMER_PORTAL_URL + portalUrl || "";
            break;
          default:
            portalUrl = redirectUrl;
            break;
        }
      }
      setActivePortal(portalName);

      if (token) {
        const storedLanguage = localStorage.getItem("selectedLang") || DEFAULT_LANGUAGE;
        const fullUrl = `${portalUrl}?access_token=${token}&token_expiry=${access_token_expiry}&redirect_url=${window.location.origin}&language=${storedLanguage}`;
        window.location.href = fullUrl;
      } else {
        navigate("/");
      }
    }
  };

  return (
    <>
      <div
        className={`sidebar ${sidebarVisible ? "sidebar-hide" : "sidebar-show "}${footerMenuVisible ? "footer-menu-show" : ""}`}
      >
        <div className="sidebar-header">
          <a className="sidebar-logo" onClick={onHomeClick}>
            <img src={logo} alt="" />
          </a>
          <span id="menuSidebar" className="menu-link" onClick={toggleSidebar}>
            <i className="bi bi-list"></i>
          </span>
        </div>

        {/* User Info */}
        <div className="p-1 ms-3">
          <div className="d-flex align-items-center">
            <div className="text-truncate" style={{ minWidth: 0 }}>
              <h6 className="mb-0 text-truncate fs-18">
                {user?.firstName} {user?.lastName}
              </h6>
              <small className="text-muted text-truncate">{user?.email}</small>
            </div>
          </div>
        </div>

        <div id="sidebarMenu" className="sidebar-body">
          <a className={`nav-d-label ${matchHomePage ? "active" : "inactive"}`} onClick={onHomeClick}>
            <img src={HomeIcon} alt="dashicon" /> Home
          </a>
          {userRoles.isAdmin && (
            <>
              <a className={`nav-d-label ${isLogoPortalActive ? "active" : "inactive"}`}
                onClick={() => handleSelectionPortal("Logo Portal")}
              >
                <img
                  src={
                    activePortal === "Logo Portal"
                      ? ChecklistActiveIcon
                      : ChecklistInActiveIcon
                  }
                  alt="dashicon"
                />
                Logo Portal
              </a>
              <a
                className={`nav-d-label ${activePortal === "Customer Portal" ? "active" : "inactive"}`}
                onClick={() => handleSelectionPortal("Customer Portal")}
              >
                <img
                  src={
                    activePortal === "Customer Portal"
                      ? dashIcon
                      : DashInactiveIcon
                  }
                  alt="dashicon"
                />
                Customer Portal
              </a>
              <a
                className={`nav-d-label ${activePortal === "Auditor Portal" ? "active" : "inactive"}`}
                onClick={() => handleSelectionPortal("Auditor Portal")}
              >
                <img
                  src={
                    activePortal === "Auditor Portal"
                      ? dashIcon
                      : DashInactiveIcon
                  }
                  alt="Auditor Portal"
                />
                Auditor Portal
              </a>
            </>
          )}
          {userRoles.isAuditor && (
            <a
              className={`nav-d-label ${activePortal === "Auditor Portal" ? "active" : "inactive"}`}
              onClick={() => handleSelectionPortal("Auditor Portal")}
            >
              <img
                src={
                  activePortal === "Auditor Portal"
                    ? dashIcon
                    : DashInactiveIcon
                }
                alt="Auditor Portal"
              />
              Auditor Portal
            </a>
          )}
          {userRoles.isCustomer && (
            <>
              <a
                className={`nav-d-label ${activePortal === "Customer Portal" ? "active" : "inactive"}`}
                onClick={() => handleSelectionPortal("Customer Portal")}
              >
                <img
                  src={
                    activePortal === "Customer Portal"
                      ? dashIcon
                      : DashInactiveIcon
                  }
                  alt="dashicon"
                />
                Customer Portal
              </a>
            </>
          )}
          {(userRoles.isLogoPortalUser || userRoles.isLogoPortalSubUser) &&
            <a className={`nav-d-label ${isLogoPortalActive ? "active" : "inactive"}`}
            onClick={() => handleSelectionPortal("Logo Portal")}>
            <img
              src={
                activePortal === "Logo Portal"
                  ? ChecklistActiveIcon
                  : ChecklistInActiveIcon
              }
              alt="dashicon"
            />
            Logo Portal
          </a>}
        </div>
        <div className="sidebar-footer">
          <div id="sidebarMenu" className="sidebar-body">
            {(userRoles.isAdmin || userRoles.isLogoPortalUser) && (
              <a className={`nav-label ${ matchManageUserPage ? "active" : "inactive"}`}
                style={{ cursor: 'pointer' }}
                onClick={onManageUserClick}
              >
                <FaUserCogIcon size={18} /> User Management
              </a>
            )}
            {(userRoles.isAdmin || userRoles.isOpsUser) && (
              <a className={`nav-label ${ matchBannerManagementUserPage ? "active" : "inactive"}`}
                style={{ cursor: 'pointer' }}
                onClick={onBannerManagementClick}> <img src={settingIcon} />
                Banner Management
              </a>
            )}
             {(userRoles.isCustomer || userRoles.isAuditor) && (
              <a
                className="nav-label"
                href="https://scsglobalservices.atlassian.net/servicedesk/customer/portal/13"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={feedbackIcon} /> Feedback
              </a>
            )}
            <div
              className="nav-label"
              onClick={logout}
              style={{ cursor: "pointer" }}
            >
              <img src={logoutIcon} /> Log out{" "}
            </div>
            <div className="language-container" style={{ cursor: 'pointer' }}> <Globe className="me-2" />Language :
              <div className="ps-2" id="weglot_here"></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;